// Global application state using Zustand

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, World, WorldState, Message, Character, CompanionProfile, CompanionState } from '../types';
import { getCurrentUser, getWorlds } from '../api';

// ============================================
// User Preferences Store (persisted)
// ============================================

export type ThemeMode = 'dark' | 'light' | 'system';
export type FontFamily = 'system' | 'inter' | 'roboto' | 'georgia' | 'merriweather';
export type FontSize = 'small' | 'medium' | 'large' | 'xlarge';

interface UserPreferencesState {
  theme: ThemeMode;
  fontFamily: FontFamily;
  fontSize: FontSize;
  setTheme: (theme: ThemeMode) => void;
  setFontFamily: (font: FontFamily) => void;
  setFontSize: (size: FontSize) => void;
}

export const useUserPreferencesStore = create<UserPreferencesState>()(
  persist(
    (set) => ({
      theme: 'dark',
      fontFamily: 'system',
      fontSize: 'medium',
      
      setTheme: (theme) => set({ theme }),
      setFontFamily: (fontFamily) => set({ fontFamily }),
      setFontSize: (fontSize) => set({ fontSize }),
    }),
    {
      name: 'textborne-preferences',
    }
  )
);

// Font family CSS mappings
export const FONT_FAMILIES: Record<FontFamily, string> = {
  system: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif',
  inter: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
  roboto: '"Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
  georgia: '"Georgia", "Times New Roman", serif',
  merriweather: '"Merriweather", Georgia, serif',
};

// Font size CSS mappings (for narrative text)
export const FONT_SIZES: Record<FontSize, string> = {
  small: '12px',
  medium: '14px',
  large: '16px',
  xlarge: '18px',
};

// ============================================
// Auth Store
// ============================================

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  checkAuth: () => Promise<void>;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  
  setUser: (user) => set({ 
    user, 
    isAuthenticated: !!user,
    isLoading: false 
  }),
  
  checkAuth: async () => {
    set({ isLoading: true });
    try {
      const user = await getCurrentUser();
      set({ user, isAuthenticated: !!user, isLoading: false });
    } catch {
      set({ user: null, isAuthenticated: false, isLoading: false });
    }
  },
  
  logout: () => {
    localStorage.removeItem('token');
    set({ user: null, isAuthenticated: false });
  },
}));

interface WorldsState {
  worlds: World[];
  isLoading: boolean;
  error: string | null;
  fetchWorlds: () => Promise<void>;
  addWorld: (world: World) => void;
  removeWorld: (worldId: string) => void;
}

export const useWorldsStore = create<WorldsState>((set) => ({
  worlds: [],
  isLoading: false,
  error: null,
  
  fetchWorlds: async () => {
    set({ isLoading: true, error: null });
    try {
      const worlds = await getWorlds();
      set({ worlds, isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch worlds',
        isLoading: false 
      });
    }
  },
  
  addWorld: (world) => set((state) => ({ 
    worlds: [world, ...state.worlds] 
  })),
  
  removeWorld: (worldId) => set((state) => ({
    worlds: state.worlds.filter(w => w.id !== worldId)
  })),
}));

// Simulation status for buffered mode
export interface SimulationStatus {
  // Narrative phases: context, generation, guardrails, complete
  // Companion phases: scaffolding, preflight, generation, state, complete
  phase: 'context' | 'generation' | 'guardrails' | 'complete' | 'scaffolding' | 'preflight' | 'state' | 'postprocessing';
  status: string;
  icon: string;
  classification?: string;
}

// ============================================
// Ephemeral chat notices (inline, non-persisted)
// Used for errors, connection issues, budget warnings — anything transient
// the user needs to see in context but that isn't part of the saved history.
// ============================================

export interface EphemeralNoticeErrorDetails {
  code: string;
  phase?: string;
  provider?: string;
  model?: string;
  /** Raw technical message from the backend, surfaced under "Details". */
  rawMessage: string;
}

export interface EphemeralNotice {
  id: string;
  worldId: string;
  /** 'error' renders as a callout; 'info' renders as a muted note. */
  kind: 'error' | 'info';
  /** One-line user-facing sentence. Never the raw error code or model ID. */
  title: string;
  /** Present for error notices; hidden until the user expands Details. */
  errorDetails?: EphemeralNoticeErrorDetails;
  created_at: string;
}

/** Input shape for `addEphemeralNotice` — worldId/id/created_at are filled in by the action. */
export type EphemeralNoticeInput =
  | string
  | (Omit<EphemeralNotice, 'id' | 'worldId' | 'created_at'> & { kind?: 'error' | 'info' });

interface ActiveWorldState {
  worldState: WorldState | null;
  pendingActiveCharacters: Character[] | null;
  pendingSceneLocation: { value: string | null } | null;
  messages: Message[];
  // Scroll-back pagination state
  isLoadingOlderHistory: boolean;
  hasMoreHistory: boolean;
  /** Ephemeral chat notices (errors, budget warnings) shown inline but never persisted. */
  ephemeralNotices: EphemeralNotice[];
  isConnected: boolean;
  isStreaming: boolean;
  streamingContent: string;
  error: string | null;
  // Current chapter number (fetched from API)
  currentChapterNumber: number | null;
  // Buffered mode: simulation status
  simulationStatus: SimulationStatus | null;
  // Input history for arrow key navigation
  inputHistory: string[];
  inputHistoryIndex: number;
  // Track pending message for restoration on error
  pendingInputMessageId: string | null;
  pendingInputContent: string | null;
  // Input restored after error (NarrativeView watches this)
  restoredInput: string | null;
  // Session cost tracking (microdollars)
  sessionCost: {
    promptTokens: number;
    completionTokens: number;
    totalCostMicros: number;
  };
  // Total cumulative cost (loaded from world)
  totalCost: {
    promptTokens: number;
    completionTokens: number;
    totalCostMicros: number;
  };
  // Credits balance for status bar
  creditsBalanceMicros: number | null;
  // P1.2: Companion profile for companion simulation
  companionProfile: CompanionProfile | null;
  // P2.1: Companion state (mood, relationship) for status bar
  companionState: CompanionState | null;

  // Rail world facts: active plot thread (from world entity_memories)
  worldFacts: { activePlotThread: string | null };
  // Rail recent beats: distinct event tags from the last few turns
  recentBeats: { events: string[]; turnSpan: { start: number; end: number } | null };
  // Rail chapter spine: compact chapter list + recall-glow set
  chapters: Array<{
    chapterNumber: number;
    title: string;
    primaryLocation: string | null;
    status: string;
  }>;
  // Set of chapter numbers currently glowing from a recall_fired event.
  // Cleared per-chapter via setTimeout in the WS handler.
  glowingChapters: Set<number>;

  setWorldState: (state: WorldState | null) => void;
  setConnected: (connected: boolean) => void;
  setStreaming: (streaming: boolean) => void;
  appendStreamContent: (content: string) => void;
  setStreamingContent: (content: string) => void;
  clearStreamContent: () => void;
  addMessage: (message: Message) => void;
  setMessages: (messages: Message[]) => void;
  prependOlderMessages: (older: Message[]) => void;
  setLoadingOlderHistory: (loading: boolean) => void;
  setHasMoreHistory: (hasMore: boolean) => void;
  updateMessage: (messageId: string, content: string, inferenceLogId?: string | null) => void;
  removeMessage: (messageId: string) => void;
  setActiveCharacters: (characters: Character[], location?: string | null) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  addEphemeralNotice: (notice: EphemeralNoticeInput) => void;
  removeEphemeralNotice: (id: string) => void;
  clearEphemeralNotices: () => void;
  setCurrentChapterNumber: (num: number | null) => void;
  // Simulation status actions
  setSimulationStatus: (status: SimulationStatus | null) => void;
  // Input history actions
  pushInputHistory: (input: string) => void;
  setInputHistoryIndex: (index: number) => void;
  clearInputHistoryNavigation: () => void;
  // Pending input for error recovery
  setPendingInput: (messageId: string, content: string) => void;
  clearPendingInput: () => void;
  restoreLastInput: () => { content: string; messageId: string } | null;
  clearRestoredInput: () => void;
  // Session cost tracking
  addSessionCost: (promptTokens: number, completionTokens: number, costMicros: number) => void;
  resetSessionCost: () => void;
  // Total cost (from world)
  setTotalCost: (promptTokens: number, completionTokens: number, costMicros: number) => void;
  setCreditsBalance: (balanceMicros: number | null) => void;
  // P1.2: Companion profile
  setCompanionProfile: (profile: CompanionProfile | null) => void;
  // P2.1: Companion state (mood, relationship)
  setCompanionState: (state: CompanionState | null) => void;
  // Rail signals
  setWorldFacts: (facts: { activePlotThread: string | null }) => void;
  setRecentBeats: (beats: { events: string[]; turnSpan: { start: number; end: number } }) => void;
  setChapters: (chapters: Array<{
    chapterNumber: number;
    title: string;
    primaryLocation: string | null;
    status: string;
  }>) => void;
  upsertChapter: (chapter: {
    chapterNumber: number;
    title: string;
    primaryLocation: string | null;
    status: string;
  }) => void;
  setChapterStatus: (chapterNumber: number, status: string) => void;
  glowChapter: (chapterNumber: number) => void;
  clearChapterGlow: (chapterNumber: number) => void;
  reset: () => void;
}

export const useActiveWorldStore = create<ActiveWorldState>((set, get) => ({
  worldState: null,
  pendingActiveCharacters: null,
  pendingSceneLocation: null,
  messages: [],
  isLoadingOlderHistory: false,
  hasMoreHistory: true,
  ephemeralNotices: [],
  isConnected: false,
  isStreaming: false,
  streamingContent: '',
  error: null,
  currentChapterNumber: null,
  simulationStatus: null,
  inputHistory: [],
  inputHistoryIndex: -1,
  pendingInputMessageId: null,
  pendingInputContent: null,
  restoredInput: null,
  sessionCost: {
    promptTokens: 0,
    completionTokens: 0,
    totalCostMicros: 0,
  },
  totalCost: {
    promptTokens: 0,
    completionTokens: 0,
    totalCostMicros: 0,
  },
  creditsBalanceMicros: null,
  companionProfile: null,  // P1.2: Companion profile state
  companionState: null,    // P2.1: Companion state for status bar
  worldFacts: { activePlotThread: null },
  recentBeats: { events: [], turnSpan: null },
  chapters: [],
  glowingChapters: new Set<number>(),

  setWorldState: (worldState) => set((state) => {
    if (!worldState) {
      return { worldState: null };
    }
    const mergedActiveCharacters = state.pendingActiveCharacters ?? worldState.activeCharacters;
    return {
      worldState: {
        ...worldState,
        activeCharacters: mergedActiveCharacters,
        seed: worldState.seed && state.pendingSceneLocation
          ? { ...worldState.seed, current_location: state.pendingSceneLocation.value } : worldState.seed,
      },
      pendingActiveCharacters: null,
      pendingSceneLocation: null,
    };
  }),
  setConnected: (isConnected) => set({ isConnected }),
  setStreaming: (isStreaming) => set({ isStreaming }),
  appendStreamContent: (content) => set((state) => ({
    streamingContent: state.streamingContent + content
  })),
  setStreamingContent: (content) => set({ streamingContent: content }),
  clearStreamContent: () => set({ streamingContent: '' }),
  addMessage: (message) => set((state) => {
    // Deduplicate by ID — safeSend broadcasts to all sessions for a user,
    // so multiple WS connections (e.g. StrictMode double-mount) can deliver the same message twice
    if (state.messages.some(m => m.id === message.id)) {
      return state;
    }
    return { messages: [...state.messages, message] };
  }),
  setMessages: (messages) => set({ messages }),
  prependOlderMessages: (older) => set((state) => {
    // Dedupe by id against what we already hold (defense against retries or
    // overlapping cursors). Incoming `older` are oldest-first from the server.
    const existingIds = new Set(state.messages.map(m => m.id));
    const unique = older.filter(m => !existingIds.has(m.id));
    if (unique.length === 0) return state;
    return { messages: [...unique, ...state.messages] };
  }),
  setLoadingOlderHistory: (isLoadingOlderHistory) => set({ isLoadingOlderHistory }),
  setHasMoreHistory: (hasMoreHistory) => set({ hasMoreHistory }),
  updateMessage: (messageId, content, inferenceLogId) => set((state) => ({
    messages: state.messages.map(m => 
      m.id === messageId ? { 
        ...m, 
        content,
        ...(inferenceLogId !== undefined && { inference_log_id: inferenceLogId })
      } : m
    )
  })),
  removeMessage: (messageId) => set((state) => ({
    messages: state.messages.filter(m => m.id !== messageId)
  })),
  setActiveCharacters: (characters, location) => set((state) => {
    if (!state.worldState) {
      return { pendingActiveCharacters: characters,
        ...(location !== undefined ? { pendingSceneLocation: { value: location } } : {}) };
    }
    return {
      worldState: {
        ...state.worldState,
        activeCharacters: characters,
        seed: state.worldState.seed && location !== undefined
          ? { ...state.worldState.seed, current_location: location } : state.worldState.seed,
      },
    };
  }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),
  addEphemeralNotice: (input) => set((state) => {
    const worldId = state.worldState?.world?.id ?? state.worldState?.worldId ?? '';
    const base = {
      id: `eph_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      worldId,
      created_at: new Date().toISOString(),
    };
    const notice: EphemeralNotice =
      typeof input === 'string'
        ? { ...base, kind: 'info', title: input }
        : { ...base, kind: input.kind ?? 'info', title: input.title, errorDetails: input.errorDetails };
    return { ephemeralNotices: [...state.ephemeralNotices, notice] };
  }),
  removeEphemeralNotice: (id) => set((state) => ({
    ephemeralNotices: state.ephemeralNotices.filter((n) => n.id !== id),
  })),
  clearEphemeralNotices: () => set({ ephemeralNotices: [] }),
  setCurrentChapterNumber: (currentChapterNumber) => set({ currentChapterNumber }),
  // Simulation status actions
  setSimulationStatus: (status) => set({ simulationStatus: status }),
  // Input history actions
  pushInputHistory: (input) => set((state) => ({
    inputHistory: [...state.inputHistory, input],
    inputHistoryIndex: -1,
  })),
  setInputHistoryIndex: (index) => set({ inputHistoryIndex: index }),
  clearInputHistoryNavigation: () => set({ inputHistoryIndex: -1 }),
  // Pending input for error recovery
  setPendingInput: (messageId, content) => set({ pendingInputMessageId: messageId, pendingInputContent: content }),
  clearPendingInput: () => set({ pendingInputMessageId: null, pendingInputContent: null }),
  restoreLastInput: (): { content: string; messageId: string } | null => {
    const state = get();
    if (state.pendingInputContent && state.pendingInputMessageId) {
      const result = { content: state.pendingInputContent, messageId: state.pendingInputMessageId };
      // Remove the optimistic message, clear pending state, set restored input
      set((s) => ({
        messages: s.messages.filter(m => m.id !== state.pendingInputMessageId),
        pendingInputMessageId: null,
        pendingInputContent: null,
        // Also remove from history since it wasn't actually sent
        inputHistory: s.inputHistory.slice(0, -1),
        // Set restored input for NarrativeView to pick up
        restoredInput: state.pendingInputContent,
      }));
      return result;
    }
    return null;
  },
  clearRestoredInput: () => set({ restoredInput: null }),
  // Session cost tracking
  addSessionCost: (promptTokens, completionTokens, costMicros) => set((state) => ({
    sessionCost: {
      promptTokens: state.sessionCost.promptTokens + promptTokens,
      completionTokens: state.sessionCost.completionTokens + completionTokens,
      totalCostMicros: state.sessionCost.totalCostMicros + costMicros,
    },
  })),
  resetSessionCost: () => set({
    sessionCost: { promptTokens: 0, completionTokens: 0, totalCostMicros: 0 },
  }),
  // Total cost
  setTotalCost: (promptTokens, completionTokens, costMicros) => set({
    totalCost: { promptTokens, completionTokens, totalCostMicros: costMicros },
  }),
  setCreditsBalance: (balanceMicros) => set({ creditsBalanceMicros: balanceMicros }),
  // P1.2: Companion profile action
  setCompanionProfile: (companionProfile) => set({ companionProfile }),
  setCompanionState: (companionState) => set({ companionState }),
  // Rail signals
  setWorldFacts: (worldFacts) => set({ worldFacts }),
  setRecentBeats: (recentBeats) => set({ recentBeats }),
  setChapters: (chapters) => set({ chapters }),
  upsertChapter: (chapter) => set((state) => {
    const idx = state.chapters.findIndex((c) => c.chapterNumber === chapter.chapterNumber);
    if (idx >= 0) {
      const next = [...state.chapters];
      next[idx] = chapter;
      return { chapters: next };
    }
    return { chapters: [...state.chapters, chapter].sort((a, b) => a.chapterNumber - b.chapterNumber) };
  }),
  setChapterStatus: (chapterNumber, status) => set((state) => ({
    chapters: state.chapters.map((c) =>
      c.chapterNumber === chapterNumber ? { ...c, status } : c,
    ),
  })),
  glowChapter: (chapterNumber) => set((state) => {
    const next = new Set(state.glowingChapters);
    next.add(chapterNumber);
    return { glowingChapters: next };
  }),
  clearChapterGlow: (chapterNumber) => set((state) => {
    if (!state.glowingChapters.has(chapterNumber)) return state;
    const next = new Set(state.glowingChapters);
    next.delete(chapterNumber);
    return { glowingChapters: next };
  }),
  reset: () => set({
    worldState: null,
    pendingActiveCharacters: null,
    pendingSceneLocation: null,
    messages: [],
    isLoadingOlderHistory: false,
    hasMoreHistory: true,
    ephemeralNotices: [],
    isConnected: false,
    isStreaming: false,
    streamingContent: '',
    error: null,
    currentChapterNumber: null,
    simulationStatus: null,
    inputHistory: [],
    inputHistoryIndex: -1,
    pendingInputMessageId: null,
    pendingInputContent: null,
    restoredInput: null,
    sessionCost: { promptTokens: 0, completionTokens: 0, totalCostMicros: 0 },
    totalCost: { promptTokens: 0, completionTokens: 0, totalCostMicros: 0 },
    creditsBalanceMicros: null,
    companionProfile: null,  // P1.2: Reset companion profile
    companionState: null,    // P2.1: Reset companion state
    worldFacts: { activePlotThread: null },
    recentBeats: { events: [], turnSpan: null },
    chapters: [],
    glowingChapters: new Set<number>(),
  }),
}));
// ============================================
// Toast Store (for notifications)
// ============================================

export interface Toast {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  action?: { label: string; href: string };
  duration?: number; // ms, 0 = permanent
}

interface ToastState {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  
  addToast: (toast) => set((state) => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const newToast: Toast = { id, ...toast };
    
    // Auto-remove after duration (default 5s for non-permanent)
    if (toast.duration !== 0) {
      setTimeout(() => {
        set((s) => ({ toasts: s.toasts.filter(t => t.id !== id) }));
      }, toast.duration || 5000);
    }
    
    return { toasts: [...state.toasts, newToast] };
  }),
  
  removeToast: (id) => set((state) => ({
    toasts: state.toasts.filter(t => t.id !== id)
  })),
  
  clearToasts: () => set({ toasts: [] }),
}));
